import Phaser from 'phaser';
import { GRID, MATCH } from '@snake-royalle/shared';
import { MiniMap } from '../effects/MiniMap.js';
import { ChestProgressWidget } from '../effects/ChestProgressWidget.js';
import { TopPlayersWidget } from '../effects/TopPlayersWidget.js';
import { KeyboardHintOverlay } from '../effects/KeyboardHintOverlay.js';

interface SpectatePerkOption { id: string; name: string; description?: string; rarity?: string }

/** HUD overlay scene — coin counter, timer, snake length, kill feed, perk indicator */
export class HUDScene extends Phaser.Scene {
  private coinText!: Phaser.GameObjects.Text;
  private coinIcon!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private lengthText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private killFeed: Phaser.GameObjects.Text[] = [];
  private perkText!: Phaser.GameObjects.Text;
  private lastCoinCount: number = 0;
  private miniMap: MiniMap | null = null;
  private chestWidget: ChestProgressWidget | null = null;
  private topPlayers: TopPlayersWidget | null = null;
  private latencyText: Phaser.GameObjects.Text | null = null;
  private kbdHint: KeyboardHintOverlay | null = null;
  /** Spectator decision overlay (perk cards / chest reveal). HUD camera is
   *  zoom 1, so these render in true screen-space. */
  private spectatePerkLayer: Phaser.GameObjects.GameObject[] = [];
  private spectatePerkCards: Map<string, Phaser.GameObjects.Container> = new Map();
  private spectateChestLayer: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'HUDScene' });
  }

  create(data?: { perkName?: string; demoMode?: boolean; spectate?: boolean }): void {
    const padding = 16;
    // Coins live BELOW the top-players widget at top-left.
    // TopPlayersWidget anchors at (16, 16) and is now ~190px tall.
    const coinsY = 220;

    // Coin icon + counter (top-left, below TopPlayersWidget)
    this.coinIcon = this.add.text(padding, coinsY, '$', {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#ffdd00',
      stroke: '#000',
      strokeThickness: 3,
    }).setScrollFactor(0).setDepth(100);

    this.coinText = this.add.text(padding + 28, coinsY, '0', {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#ffdd00',
      stroke: '#000',
      strokeThickness: 3,
    }).setScrollFactor(0).setDepth(100);

    // Timer — placed to the RIGHT of the chest widget so it stays visible
    // without competing for the center band with the chain announcer.
    // chestWidgetHalfW (160) + small gap.
    this.timerText = this.add.text(this.scale.width / 2 + 196, 38, '10:00', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#aab4c8',
      stroke: '#000',
      strokeThickness: 3,
    }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(100);

    // Next-chest progress widget — top-center
    this.chestWidget = new ChestProgressWidget(this);

    // Live arena leaderboard — top-left
    this.topPlayers = new TopPlayersWidget(this);

    // Snake length (top-right)
    this.lengthText = this.add.text(this.scale.width - padding, padding, 'LEN: 3', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#00ff88',
      stroke: '#000',
      strokeThickness: 2,
    }).setOrigin(1, 0).setScrollFactor(0).setDepth(100);

    // Perk indicator (below length)
    this.perkText = this.add.text(this.scale.width - padding, padding + 24, '', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ff00ff',
      stroke: '#000',
      strokeThickness: 2,
    }).setOrigin(1, 0).setScrollFactor(0).setDepth(100);

    // Status (bottom-center)
    this.statusText = this.add.text(this.scale.width / 2, this.scale.height - padding, '', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#888888',
    }).setOrigin(0.5, 1).setScrollFactor(0).setDepth(100);

    const onLatencyUpdate = (latency: { pingMs: number; jitterMs: number }) => {
      this.latencyText?.setText(`PING ${latency.pingMs}ms  JIT ${latency.jitterMs}ms`);
    };

    if (data?.demoMode) {
      this.latencyText = this.add.text(padding, 244, 'PING --ms  JIT --ms', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#b8f7ff',
        stroke: '#000000',
        strokeThickness: 3,
        fontStyle: 'bold',
      }).setOrigin(0, 0).setScrollFactor(0).setDepth(160);

      this.game.events.on('latencyUpdate', onLatencyUpdate);
    }

    // Listen for kill feed events from game scene
    this.game.events.on('killFeed', (data: { killerName: string; victimName: string; coinsStolen: number }) => {
      this.addKillFeedEntry(data.killerName, data.victimName, data.coinsStolen);
    });

    // Mini-map / radar — lives in HUDScene because GameScene's camera is at
    // zoom 2x and setScrollFactor(0) doesn't compensate for zoom. HUD camera
    // is zoom 1 by default, so screen-space coords work directly.
    //
    // Dims come from the registry (set by GameScene from joinData), with
    // a fallback to the static GRID constant for the brief startup window
    // before GameScene has wired the registry.
    const initialW = (this.registry.get('gridWidth') as number | undefined) ?? GRID.width;
    const initialH = (this.registry.get('gridHeight') as number | undefined) ?? GRID.height;
    this.miniMap = new MiniMap(this, initialW, initialH, 'bottom-right');
    const onMiniData = (data: any) => {
      this.miniMap?.draw(data);
    };
    this.game.events.on('minimapData', onMiniData);

    // Adaptive arena: server may grow the grid mid-match. Re-size the
    // minimap so its viewport keeps matching the world dims.
    const onGridResized = (size: { width: number; height: number }) => {
      this.miniMap?.resize(size.width, size.height);
    };
    this.game.events.on('gridResized', onGridResized);

    // Keyboard onboarding hint — owned here (HUD camera is zoom 1) so
    // it actually pins to screen-space. GameScene fires the events.
    const onCountdownStart = () => {
      if (!this.shouldShowKbdHint(!!data?.demoMode)) return;
      this.kbdHint = new KeyboardHintOverlay(this);
      this.kbdHint.show();
      this.bumpKbdHintCount();
    };
    const onCountdownEnd = () => {
      this.kbdHint?.hide();
      this.kbdHint = null;
    };
    this.game.events.on('countdown:start', onCountdownStart);
    this.game.events.on('countdown:end', onCountdownEnd);

    // Spectator decision moments — mirror the followed agent's held decision
    // (perk cards while it chooses; chest reveal when it opens). GameScene
    // fires these off the agent's state; the spectator picks nothing.
    const onPerkOpen = (d: { options: SpectatePerkOption[] }) => this.showSpectatePerkOverlay(d.options);
    const onPerkChosen = (d: { chosenOptionId: string }) => this.highlightSpectatePerk(d.chosenOptionId);
    const onDecisionClose = () => this.closeSpectatePerkOverlay();
    const onChestReveal = (d: { chestResult: unknown }) => this.showSpectateChestReveal(d.chestResult);
    const onEliminated = () => this.showSpectateEliminatedBanner();
    if (data?.spectate) {
      this.game.events.on('spectate:perkOpen', onPerkOpen);
      this.game.events.on('spectate:perkChosen', onPerkChosen);
      this.game.events.on('spectate:decisionClose', onDecisionClose);
      this.game.events.on('spectate:chestReveal', onChestReveal);
      this.game.events.on('spectate:eliminated', onEliminated);
    }

    // Tear down listener + minimap when this scene stops
    this.events.once('shutdown', () => {
      this.game.events.off('latencyUpdate', onLatencyUpdate);
      this.latencyText?.destroy();
      this.latencyText = null;
      this.game.events.off('minimapData', onMiniData);
      this.game.events.off('gridResized', onGridResized);
      this.game.events.off('countdown:start', onCountdownStart);
      this.game.events.off('countdown:end', onCountdownEnd);
      this.game.events.off('spectate:perkOpen', onPerkOpen);
      this.game.events.off('spectate:perkChosen', onPerkChosen);
      this.game.events.off('spectate:decisionClose', onDecisionClose);
      this.game.events.off('spectate:chestReveal', onChestReveal);
      this.game.events.off('spectate:eliminated', onEliminated);
      this.destroySpectatePerkOverlay();
      this.spectateChestLayer.forEach((o) => o.destroy());
      this.spectateChestLayer = [];
      this.kbdHint?.destroy();
      this.kbdHint = null;
      this.miniMap?.destroy();
      this.miniMap = null;
      this.chestWidget?.destroy();
      this.chestWidget = null;
      this.topPlayers?.destroy();
      this.topPlayers = null;
    });

    // Handle resize (chest widget recenters itself in its own resize hook)
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      this.timerText.setX(gameSize.width / 2 + 196);
      this.lengthText.setX(gameSize.width - padding);
      this.perkText.setX(gameSize.width - padding);
      this.statusText.setPosition(gameSize.width / 2, gameSize.height - padding);
    });
  }

  /** Gate for the keyboard hint overlay during countdown. Returns true
   *  for first-time players (count < 3), demo mode, or `?hint=1`. Always
   *  false on mobile (no keyboard) or `?hint=0`. */
  private shouldShowKbdHint(demoMode: boolean): boolean {
    if (this.registry.get('isMobile')) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('hint') === '0') return false;
    if (params.get('hint') === '1' || demoMode) return true;
    try {
      const count = Number(localStorage.getItem('snake_kbd_hint_count') || '0');
      return count < 3;
    } catch {
      return true; // localStorage blocked → assume first-time
    }
  }

  private bumpKbdHintCount(): void {
    try {
      const n = Number(localStorage.getItem('snake_kbd_hint_count') || '0');
      localStorage.setItem('snake_kbd_hint_count', String(n + 1));
    } catch {
      /* ignore */
    }
  }

  update(): void {
    this.game.events.emit('requestGameData', (data: {
      tick: number;
      coins: number;
      totalBanked: number;
      extractionsUsed: number;
      isAlive: boolean;
      snakeLength: number;
      perkName?: string;
    }) => {
      // Animate coin counter on change
      if (data.coins !== this.lastCoinCount) {
        const diff = data.coins - this.lastCoinCount;
        if (diff > 0) {
          this.punchScale(this.coinText);
        }
        this.lastCoinCount = data.coins;
      }

      // Stake multiplier for display. The underlying snake state is
      // unmultiplied (chest thresholds compute off raw banked+coins),
      // but the player's "what's my score?" surface should reflect
      // the N× they paid for at entry.
      const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
      const displayCoins = data.coins * stake;
      const displayBanked = data.totalBanked * stake;
      const stakeBadge = stake > 1 ? ` ×${stake}` : '';

      // Show coins in hand + banked total + extraction count
      const bankedStr = displayBanked > 0 ? ` (${displayBanked} banked)` : '';
      this.coinText.setText(`${displayCoins}${stakeBadge}${bankedStr}`);
      const extStr = data.extractionsUsed > 0 ? ` | EXT: ${data.extractionsUsed}/3` : '';
      this.lengthText.setText(`LEN: ${data.snakeLength}${extStr}`);

      // Chest progress uses RAW totals — the thresholds (10/25/50/100)
      // are defined against unmultiplied coin counts, so the bar must
      // reflect what the server actually evaluates against.
      this.chestWidget?.update(data.totalBanked + data.coins);

      if (data.perkName) {
        this.perkText.setText(data.perkName);
      }

      // Timer
      const totalTicks = MATCH.durationSeconds * MATCH.tickRate;
      const remaining = Math.max(0, totalTicks - data.tick);
      const seconds = Math.ceil(remaining / MATCH.tickRate);
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      this.timerText.setText(`${min}:${sec.toString().padStart(2, '0')}`);

      this.timerText.setColor('#ffffff');

      if (!data.isAlive) {
        this.statusText.setText('SPECTATING');
      }
    });

    // Pull leaderboard data each frame and feed the widget.
    this.game.events.emit('requestLeaderboardData', (lb: {
      entries: Array<{ id: string; name: string; score: number; isBot: boolean; isMe: boolean }>;
      mySnakeId: string;
    }) => {
      this.topPlayers?.update(lb);
    });
  }

  private killFeedY(index: number): number {
    // Bottom-left, oldest at the top of the stack so newest is closest
    // to the bottom (~16px above the screen edge).
    return this.scale.height - 16 - (this.killFeed.length - 1 - index) * 20;
  }

  private addKillFeedEntry(killer: string, victim: string, coins: number): void {
    const padding = 16;

    const text = this.add.text(padding, 0, `${killer} killed ${victim} (+${coins})`, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ff8844',
      stroke: '#000',
      strokeThickness: 2,
    }).setOrigin(0, 1).setScrollFactor(0).setDepth(100).setAlpha(0);

    this.killFeed.push(text);
    // Position all entries (the new one + reposition existing)
    this.killFeed.forEach((t, i) => t.setY(this.killFeedY(i)));

    // Fade in
    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 200,
    });

    // Fade out after 3 seconds
    this.time.delayedCall(3000, () => {
      this.tweens.add({
        targets: text,
        alpha: 0,
        duration: 500,
        onComplete: () => {
          const idx = this.killFeed.indexOf(text);
          if (idx >= 0) this.killFeed.splice(idx, 1);
          text.destroy();
          this.killFeed.forEach((t, i) => t.setY(this.killFeedY(i)));
        },
      });
    });

    // Cap feed at 5 entries — drop the oldest (top of stack)
    if (this.killFeed.length > 5) {
      const old = this.killFeed.shift();
      old?.destroy();
      this.killFeed.forEach((t, i) => t.setY(this.killFeedY(i)));
    }
  }

  private punchScale(target: Phaser.GameObjects.Text): void {
    this.tweens.add({
      targets: target,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 80,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  // ================= Spectator decision overlay (read-only) =================

  private destroySpectatePerkOverlay(): void {
    this.spectatePerkLayer.forEach((o) => o.destroy());
    this.spectatePerkLayer = [];
    this.spectatePerkCards.clear();
  }

  /** Show the "AGENT IS CHOOSING…" perk cards while the followed agent decides. */
  private showSpectatePerkOverlay(options: SpectatePerkOption[]): void {
    this.destroySpectatePerkOverlay();
    if (!options?.length) return;

    const { width, height } = this.scale;
    const D = 5000;

    const backdrop = this.add.rectangle(width / 2, height / 2, width, height, 0x05060c, 0.72)
      .setScrollFactor(0).setDepth(D);
    this.spectatePerkLayer.push(backdrop);

    const title = this.add.text(width / 2, height * 0.20, 'AGENT IS CHOOSING…', {
      fontFamily: 'monospace', fontSize: '22px', color: '#ffe066',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(D + 2);
    this.tweens.add({ targets: title, alpha: { from: 0.6, to: 1 }, duration: 700, yoyo: true, repeat: -1 });
    this.spectatePerkLayer.push(title);

    // Lay the cards out horizontally, centered.
    const n = Math.min(options.length, 4);
    const cardW = Math.min(170, (width - 40) / n - 16);
    const cardH = Math.min(230, height * 0.42);
    const gap = 18;
    const totalW = n * cardW + (n - 1) * gap;
    const startX = width / 2 - totalW / 2 + cardW / 2;
    const cy = height * 0.52;

    for (let i = 0; i < n; i++) {
      const opt = options[i];
      const cx = startX + i * (cardW + gap);
      const card = this.buildSpectatePerkCard(opt, cardW, cardH, D + 1);
      card.setPosition(cx, cy).setScrollFactor(0).setDepth(D + 1).setAlpha(0);
      this.tweens.add({ targets: card, alpha: 1, y: cy, duration: 260, delay: 90 * i, ease: 'Back.easeOut' });
      card.y = cy + 14;
      this.spectatePerkLayer.push(card);
      this.spectatePerkCards.set(opt.id, card);
    }
  }

  /** Build one read-only perk card as a self-contained container. */
  private buildSpectatePerkCard(opt: SpectatePerkOption, w: number, h: number, depth: number): Phaser.GameObjects.Container {
    const g = this.add.graphics();
    g.fillStyle(0x12100a, 0.98); g.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    g.lineStyle(2, 0xe6c074, 0.9); g.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);

    const name = this.add.text(0, -h / 2 + 16, opt.name.toUpperCase(), {
      fontFamily: 'monospace', fontSize: '15px', color: '#ffe4a0',
      stroke: '#000', strokeThickness: 3, fontStyle: 'bold', align: 'center',
      wordWrap: { width: w - 16 },
    }).setOrigin(0.5, 0);

    const desc = this.add.text(0, 6, opt.description ?? '', {
      fontFamily: 'monospace', fontSize: '11px', color: '#cfc6b2',
      align: 'center', wordWrap: { width: w - 20 }, lineSpacing: 3,
    }).setOrigin(0.5);

    const children: Phaser.GameObjects.GameObject[] = [g, name, desc];

    // Rarity dots (from the PerkDefinition.rarity number, if present).
    const rarity = Math.max(0, Math.min(4, parseInt(opt.rarity ?? '', 10) || 0));
    if (rarity > 0) {
      const dots = '●'.repeat(rarity);
      const rarityText = this.add.text(0, h / 2 - 18, dots, {
        fontFamily: 'monospace', fontSize: '13px', color: '#ffcf5a',
      }).setOrigin(0.5);
      children.push(rarityText);
    }

    return this.add.container(0, 0, children).setDepth(depth);
  }

  /** Animate the agent's chosen perk, then close the overlay. */
  private highlightSpectatePerk(chosenOptionId: string): void {
    if (this.spectatePerkLayer.length === 0) return;
    for (const [id, card] of this.spectatePerkCards) {
      if (id === chosenOptionId) {
        this.tweens.add({ targets: card, scaleX: 1.12, scaleY: 1.12, duration: 220, ease: 'Back.easeOut' });
      } else {
        this.tweens.add({ targets: card, alpha: 0.28, duration: 220 });
      }
    }
    this.time.delayedCall(1200, () => this.closeSpectatePerkOverlay());
  }

  private closeSpectatePerkOverlay(): void {
    if (this.spectatePerkLayer.length === 0) return;
    const layer = this.spectatePerkLayer;
    this.spectatePerkLayer = [];
    this.spectatePerkCards.clear();
    this.tweens.add({
      targets: layer, alpha: 0, duration: 260,
      onComplete: () => layer.forEach((o) => o.destroy()),
    });
  }

  /** Banner shown when the followed agent is eliminated (vs jumping to leader). */
  private showSpectateEliminatedBanner(): void {
    const { width, height } = this.scale;
    const D = 5000;
    const cy = height * 0.16;

    const bg = this.add.rectangle(width / 2, cy, width, 54, 0x1a0606, 0.9)
      .setScrollFactor(0).setDepth(D);
    const label = this.add.text(width / 2, cy, 'AGENT ELIMINATED', {
      fontFamily: 'monospace', fontSize: '22px', color: '#ff5555',
      stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(D + 1);

    const layer: Phaser.GameObjects.GameObject[] = [bg, label];
    layer.forEach((o) => (o as unknown as Phaser.GameObjects.Components.Alpha).setAlpha(0));
    this.tweens.add({ targets: layer, alpha: 1, duration: 260 });
    this.time.delayedCall(4500, () => {
      this.tweens.add({ targets: layer, alpha: 0, duration: 400, onComplete: () => layer.forEach((o) => o.destroy()) });
    });
  }

  /** Compact chest-open reveal for the followed agent's extraction. */
  private showSpectateChestReveal(chestResult: unknown): void {
    // Tear down any previous reveal.
    this.spectateChestLayer.forEach((o) => o.destroy());
    this.spectateChestLayer = [];

    const res = chestResult as { chests?: Array<{ tier?: string; rewards?: Array<{ name?: string; rarity?: string; type?: string }> }> } | null;
    const chests = res?.chests ?? [];
    const rewards = chests.flatMap((c) => (c.rewards ?? []).map((r) => ({ ...r, tier: c.tier })));

    const { width, height } = this.scale;
    const D = 5000;
    const panelW = 320;
    const rows = Math.min(rewards.length, 3);
    const panelH = 90 + rows * 26;
    const cx = width / 2;
    const cy = height * 0.30;

    const g = this.add.graphics().setScrollFactor(0).setDepth(D);
    g.fillStyle(0x0d0b06, 0.94); g.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 12);
    g.lineStyle(2, 0xffcf5a, 0.9); g.strokeRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 12);
    this.spectateChestLayer.push(g);

    const title = this.add.text(cx, cy - panelH / 2 + 18, 'CHEST OPENED', {
      fontFamily: 'monospace', fontSize: '18px', color: '#ffe066',
      stroke: '#000', strokeThickness: 4, fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(D + 1);
    this.spectateChestLayer.push(title);

    if (rewards.length === 0) {
      const none = this.add.text(cx, cy + 6, 'Banked!', {
        fontFamily: 'monospace', fontSize: '14px', color: '#cfc6b2',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(D + 1);
      this.spectateChestLayer.push(none);
    } else {
      const rarityColor: Record<string, string> = {
        COMMON: '#cfc6b2', UNCOMMON: '#7fd8a0', RARE: '#6fb8ff', LEGENDARY: '#ffcf5a',
      };
      rewards.slice(0, 3).forEach((r, i) => {
        const line = this.add.text(cx, cy - panelH / 2 + 46 + i * 26, `${r.tier ?? ''} · ${r.name ?? r.type ?? 'Reward'}`, {
          fontFamily: 'monospace', fontSize: '13px',
          color: rarityColor[String(r.rarity)] ?? '#e8e0cf',
          stroke: '#000', strokeThickness: 2,
        }).setOrigin(0.5).setScrollFactor(0).setDepth(D + 1);
        this.spectateChestLayer.push(line);
      });
    }

    // Pop in, hold, fade out + destroy.
    this.spectateChestLayer.forEach((o) => (o as unknown as Phaser.GameObjects.Components.Alpha).setAlpha(0));
    this.tweens.add({ targets: this.spectateChestLayer, alpha: 1, duration: 220, ease: 'Back.easeOut' });
    this.time.delayedCall(3400, () => {
      const layer = this.spectateChestLayer;
      this.spectateChestLayer = [];
      this.tweens.add({ targets: layer, alpha: 0, duration: 300, onComplete: () => layer.forEach((o) => o.destroy()) });
    });
  }
}
